// ==================== CAMPAIGN ANALYTICS PAGE ====================
// Comprehensive analytics for a specific campaign

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Users,
  Send,
  CheckCircle2,
  Eye,
  XCircle,
  Clock,
  TrendingUp,
  RefreshCw,
  Calendar,
  MessageSquare,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Button, Card, Badge } from '../../../components/ui';
import { campaignsService } from '../../../services/campaigns.service';
import type { Campaign, CampaignContact } from '../../../services/campaigns.service';

// ==================== TYPES ====================
// Types are imported from campaigns.service

// ==================== HELPERS ====================

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return 'N/A';
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const diffMs = end - start;
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    SCHEDULED: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    RUNNING: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    PAUSED: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    COMPLETED: 'bg-green-500/10 text-green-400 border-green-500/20',
    CANCELLED: 'bg-red-500/10 text-red-400 border-red-500/20',
    FAILED: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  return colors[status] || colors.DRAFT;
};

const getContactStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    PENDING: 'bg-gray-500/10 text-gray-400',
    SENT: 'bg-blue-500/10 text-blue-400',
    DELIVERED: 'bg-green-500/10 text-green-400',
    READ: 'bg-emerald-500/10 text-emerald-400',
    FAILED: 'bg-red-500/10 text-red-400',
  };
  return colors[status] || colors.PENDING;
};

// ==================== MAIN COMPONENT ====================

const CampaignAnalyticsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contacts, setContacts] = useState<CampaignContact[]>([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsFilter, setContactsFilter] = useState<string>('');

  // Fetch campaign data
  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    
    try {
      const data = await campaignsService.getOne(id);
      setCampaign(data);
    } catch (err) {
      console.error('Failed to fetch campaign:', err);
      setError('Failed to load campaign data');
    }
  }, [id]);

  // Fetch campaign contacts
  const fetchContacts = useCallback(async (page = 1, status?: string) => {
    if (!id) return;
    
    try {
      const response = await campaignsService.getContacts(id, {
        page,
        limit: 20,
        status: status || undefined,
      });
      setContacts(response.items);
      setContactsTotal(response.meta.total);
      setContactsPage(page);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  }, [id]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchCampaign(), fetchContacts(1)]);
      setLoading(false);
    };
    loadData();
  }, [fetchCampaign, fetchContacts]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchCampaign(), fetchContacts(contactsPage, contactsFilter)]);
    setRefreshing(false);
  };

  // Filter contacts
  const handleFilterChange = (status: string) => {
    setContactsFilter(status);
    fetchContacts(1, status);
  };

  // Calculate rates
  const deliveryRate = campaign?.stats.total
    ? ((campaign.stats.delivered / campaign.stats.total) * 100).toFixed(1)
    : '0';
  const readRate = campaign?.stats.delivered
    ? ((campaign.stats.read / campaign.stats.delivered) * 100).toFixed(1)
    : '0';
  const failureRate = campaign?.stats.total
    ? ((campaign.stats.failed / campaign.stats.total) * 100).toFixed(1)
    : '0';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading campaign analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Analytics</h2>
          <p className="text-gray-400 mb-4">{error || 'Campaign not found'}</p>
          <Button onClick={() => navigate('/app/campaigns')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/campaigns')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{campaign.name}</h1>
              <Badge className={getStatusColor(campaign.status)}>
                {campaign.status}
              </Badge>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Campaign Analytics & Performance
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.total}</p>
              <p className="text-xs text-gray-400">Total Recipients</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-500/10">
              <Send className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.sent}</p>
              <p className="text-xs text-gray-400">Sent</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.delivered}</p>
              <p className="text-xs text-gray-400">Delivered</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Eye className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.read}</p>
              <p className="text-xs text-gray-400">Read</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.failed}</p>
              <p className="text-xs text-gray-400">Failed</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/10">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{campaign.stats.pending}</p>
              <p className="text-xs text-gray-400">Pending</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Performance Metrics */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Delivery Rate</h3>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          <p className="text-3xl font-bold text-green-400">{deliveryRate}%</p>
          <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${deliveryRate}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {campaign.stats.delivered} delivered out of {campaign.stats.total} sent
          </p>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Read Rate</h3>
            <Eye className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-emerald-400">{readRate}%</p>
          <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${readRate}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {campaign.stats.read} read out of {campaign.stats.delivered} delivered
          </p>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Failure Rate</h3>
            <XCircle className="w-4 h-4 text-red-400" />
          </div>
          <p className="text-3xl font-bold text-red-400">{failureRate}%</p>
          <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 rounded-full transition-all duration-500"
              style={{ width: `${failureRate}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {campaign.stats.failed} failed out of {campaign.stats.total} total
          </p>
        </Card>
      </motion.div>

      {/* Campaign Details */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-400" />
            Campaign Timeline
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Created</span>
              <span className="text-white">{formatDate(campaign.createdAt)}</span>
            </div>
            {campaign.scheduledAt && (
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-gray-400">Scheduled For</span>
                <span className="text-white">{formatDate(campaign.scheduledAt)}</span>
              </div>
            )}
            {campaign.startedAt && (
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-gray-400">Started</span>
                <span className="text-white">{formatDate(campaign.startedAt)}</span>
              </div>
            )}
            {campaign.pausedAt && (
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-gray-400">Paused</span>
                <span className="text-white">{formatDate(campaign.pausedAt)}</span>
              </div>
            )}
            {campaign.completedAt && (
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-gray-400">Completed</span>
                <span className="text-white">{formatDate(campaign.completedAt)}</span>
              </div>
            )}
            {campaign.startedAt && campaign.completedAt && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400">Duration</span>
                <span className="text-primary-400 font-medium">
                  {formatDuration(campaign.startedAt, campaign.completedAt)}
                </span>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-400" />
            Template Info
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Template Name</span>
              <span className="text-white">{campaign.template?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Category</span>
              <Badge variant="default">{campaign.template?.category || 'N/A'}</Badge>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Audience Type</span>
              <span className="text-white capitalize">{campaign.audienceType.replace('_', ' ').toLowerCase()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-gray-400">Batch Size</span>
              <span className="text-white">{campaign.batchSize} messages</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400">Rate Limit</span>
              <span className="text-white">{campaign.maxPerSecond} msg/sec</span>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Contact List */}
      <motion.div variants={staggerItem}>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              Recipient Details ({contactsTotal})
            </h3>
            <div className="flex items-center gap-2">
              <select
                value={contactsFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="SENT">Sent</option>
                <option value="DELIVERED">Delivered</option>
                <option value="READ">Read</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          {contacts.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No recipients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Contact</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Phone</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Sent At</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Delivered At</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Read At</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <span className="text-white text-sm">
                          {contact.contact.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-400 text-sm font-mono">
                          {contact.contact.phoneNumber}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getContactStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {contact.sentAt ? formatDate(contact.sentAt) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {contact.deliveredAt ? formatDate(contact.deliveredAt) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-400">
                        {contact.readAt ? formatDate(contact.readAt) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {contactsTotal > 20 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <p className="text-sm text-gray-400">
                Showing {((contactsPage - 1) * 20) + 1} - {Math.min(contactsPage * 20, contactsTotal)} of {contactsTotal}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchContacts(contactsPage - 1, contactsFilter)}
                  disabled={contactsPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchContacts(contactsPage + 1, contactsFilter)}
                  disabled={contactsPage * 20 >= contactsTotal}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Error Message */}
      {campaign.lastError && (
        <motion.div variants={staggerItem}>
          <Card className="p-4 border-red-500/20 bg-red-500/5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-red-400">Last Error</h4>
                <p className="text-sm text-gray-400 mt-1">{campaign.lastError}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Error count: {campaign.errorCount}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CampaignAnalyticsPage;
